/**
 * InboxGrid — the in-app notifications inbox.
 *
 * An MUI X DataGrid of {@link InboxItem}s with mark-read / dismiss row actions
 * that update optimistically (see {@link useMarkRead} / {@link useDismiss}).
 * Dismissed items are hidden; unread rows are emphasised. Each item deep-links
 * to the underlying delta hits via its document id.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { useMemo, type ReactElement } from "react";
import { useDismiss, useInbox, useMarkRead } from "./hooks/useInbox";
import type { InboxItem } from "@records-monitor/shared";
import { RouterLink } from "@/components/RouterLink";

function titleOf(payload: InboxItem["payload"]): string {
  const title = payload?.["title"];
  return typeof title === "string" ? title : "New matching documents";
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function InboxGrid(): ReactElement {
  const { data, isLoading, isError } = useInbox();
  const markRead = useMarkRead();
  const dismiss = useDismiss();

  // Hide dismissed items; keep read/unread.
  const rows = useMemo<readonly InboxItem[]>(
    () => (data?.items ?? []).filter((item) => item.dismissedAt === null),
    [data],
  );

  const columns = useMemo<GridColDef<InboxItem>[]>(
    () => [
      {
        field: "title",
        headerName: "Notification",
        flex: 1.6,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<InboxItem>) => {
          const item = params.row;
          const label = titleOf(item.payload);
          return item.documentId ? (
            <RouterLink
              to="/search"
              search={{ mode: "keyword", doc: item.documentId }}
              sx={{ fontWeight: item.readAt === null ? 700 : 400 }}
            >
              {label}
            </RouterLink>
          ) : (
            <Box sx={{ fontWeight: item.readAt === null ? 700 : 400 }}>{label}</Box>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Received",
        width: 190,
        valueGetter: (_value, row) => formatDate(row.createdAt),
      },
      {
        field: "readAt",
        headerName: "Status",
        width: 110,
        valueGetter: (_value, row) => (row.readAt === null ? "Unread" : "Read"),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 200,
        renderCell: (params: GridRenderCellParams<InboxItem>) => {
          const item = params.row;
          return (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => markRead.mutate(item.id)}
                disabled={item.readAt !== null}
              >
                Mark read
              </Button>
              <Button size="small" color="inherit" onClick={() => dismiss.mutate(item.id)}>
                Dismiss
              </Button>
            </Stack>
          );
        },
      },
    ],
    [markRead, dismiss],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress aria-label="Loading inbox" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" role="alert">
        Could not load your inbox.
      </Alert>
    );
  }

  return (
    <Box component="section" aria-label="Notifications" sx={{ width: "100%" }}>
      <DataGrid
        rows={rows as InboxItem[]}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        disableRowSelectionOnClick
        aria-label="Notifications table"
        localeText={{ noRowsLabel: "No notifications yet." }}
      />
    </Box>
  );
}
